import type { CSSProperties, ReactNode } from "react";
import {
  repartirEnLineas,
  type ColorScheme,
} from "./code-viewer-theme";

/**
 * Tokenizer + syntax highlighter para XML. Maneja:
 *  · `<!-- comentarios -->`
 *  · `<![CDATA[ ... ]]>`
 *  · `<?xml ... ?>` / `<!DOCTYPE ... >`
 *  · etiquetas normales con atributos (incluyendo cross-line)
 *  · texto entre etiquetas
 *
 * Estado interno: el tokenizer es stateful y cruza saltos de línea sin
 * perder contexto. Esto es crítico porque los CFDIs suelen partir
 * `<cfdi:Comprobante ...>` en varias líneas y un tokenizer line-by-line
 * pierde el contexto del tag abierto.
 */

export type XmlTokenType =
  | "tag"
  | "attrName"
  | "attrValue"
  | "comment"
  | "decl"
  | "cdata"
  | "text"
  | "punct";

export interface XmlToken {
  type: XmlTokenType;
  value: string;
}

export function renderXmlEnLineas(
  texto: string,
  scheme: ColorScheme
): ReactNode[][] {
  const tokens = tokenizeXmlCompleto(texto);
  return repartirEnLineas(tokens, (t) => xmlStyle(t, scheme));
}

export function tokenizeXmlCompleto(texto: string): XmlToken[] {
  const tokens: XmlToken[] = [];
  const n = texto.length;
  let i = 0;

  while (i < n) {
    // <!-- comment -->
    if (texto.startsWith("<!--", i)) {
      const end = texto.indexOf("-->", i + 4);
      const stop = end === -1 ? n : end + 3;
      tokens.push({ type: "comment", value: texto.slice(i, stop) });
      i = stop;
      continue;
    }
    // <![CDATA[ ... ]]>
    if (texto.startsWith("<![CDATA[", i)) {
      const end = texto.indexOf("]]>", i + 9);
      const stop = end === -1 ? n : end + 3;
      tokens.push({ type: "cdata", value: texto.slice(i, stop) });
      i = stop;
      continue;
    }
    // <?xml ... ?> · <!DOCTYPE ... >
    if (texto.startsWith("<?", i) || texto.startsWith("<!", i)) {
      const isPi = texto.startsWith("<?", i);
      const end = texto.indexOf(isPi ? "?>" : ">", i + 2);
      const stop = end === -1 ? n : end + (isPi ? 2 : 1);
      tokens.push({ type: "decl", value: texto.slice(i, stop) });
      i = stop;
      continue;
    }
    // Etiqueta normal — busca el `>` aunque cruce varias líneas
    if (texto[i] === "<") {
      const end = texto.indexOf(">", i);
      const stop = end === -1 ? n : end + 1;
      tokenizeTag(texto.slice(i, stop), tokens);
      i = stop;
      continue;
    }
    // Texto entre etiquetas
    const start = i;
    while (i < n && texto[i] !== "<") i++;
    if (start < i) tokens.push({ type: "text", value: texto.slice(start, i) });
  }

  return tokens;
}

function tokenizeTag(tag: string, out: XmlToken[]) {
  const n = tag.length;
  let i = 0;

  // < o </
  if (tag[i] === "<") {
    let end = i + 1;
    if (tag[end] === "/") end++;
    out.push({ type: "punct", value: tag.slice(i, end) });
    i = end;
  }

  // Nombre de la etiqueta (incluye namespace con ":")
  const nameStart = i;
  while (i < n && /[A-Za-z0-9_:.-]/.test(tag[i])) i++;
  if (i > nameStart) {
    out.push({ type: "tag", value: tag.slice(nameStart, i) });
  }

  // Atributos
  while (i < n) {
    // Whitespace
    const wsStart = i;
    while (i < n && /\s/.test(tag[i])) i++;
    if (i > wsStart) {
      out.push({ type: "text", value: tag.slice(wsStart, i) });
    }

    // Fin de etiqueta · /> o >
    if (i >= n || tag[i] === ">" || tag[i] === "/") {
      const punctStart = i;
      if (tag[i] === "/") i++;
      if (i < n && tag[i] === ">") i++;
      if (i > punctStart) {
        out.push({ type: "punct", value: tag.slice(punctStart, i) });
      }
      break;
    }

    // Nombre del atributo
    const attrStart = i;
    while (i < n && /[A-Za-z0-9_:.-]/.test(tag[i])) i++;
    if (i > attrStart) {
      out.push({ type: "attrName", value: tag.slice(attrStart, i) });
    }

    // = y valor
    if (tag[i] === "=") {
      out.push({ type: "punct", value: "=" });
      i++;
      const quote = tag[i];
      if (quote === '"' || quote === "'") {
        const valStart = i;
        i++;
        while (i < n && tag[i] !== quote) i++;
        if (i < n) i++; // incluye comilla de cierre
        out.push({ type: "attrValue", value: tag.slice(valStart, i) });
      } else {
        const valStart = i;
        while (i < n && !/[\s>/]/.test(tag[i])) i++;
        if (i > valStart) {
          out.push({ type: "attrValue", value: tag.slice(valStart, i) });
        }
      }
    }
  }
}

/**
 * Paleta de colores por tipo de token · adaptiva al color scheme.
 * Light inspirado en GitHub Light · dark en VSCode Dark+. Evita rojos
 * puros y cyans neón sobre blanco; evita saturados sobre fondo oscuro.
 */
const XML_COLORS: Record<ColorScheme, Record<XmlTokenType, string>> = {
  light: {
    tag: "#0550ae",
    attrName: "#8250df",
    attrValue: "#0f766e",
    comment: "#6e7781",
    decl: "#9a3412",
    cdata: "#9a3412",
    punct: "#57606a",
    text: "#1e293b",
  },
  dark: {
    tag: "#7dd3fc",
    attrName: "#c4b5fd",
    attrValue: "#86efac",
    comment: "#64748b",
    decl: "#fbbf24",
    cdata: "#fb923c",
    punct: "#94a3b8",
    text: "#e2e8f0",
  },
};

function xmlStyle(type: XmlTokenType, scheme: ColorScheme): CSSProperties {
  const palette = XML_COLORS[scheme];
  switch (type) {
    case "tag":
      return { color: palette.tag, fontWeight: 500 };
    case "attrName":
      return { color: palette.attrName };
    case "attrValue":
      return { color: palette.attrValue };
    case "comment":
      return { color: palette.comment, fontStyle: "italic" };
    case "decl":
      return { color: palette.decl };
    case "cdata":
      return { color: palette.cdata };
    case "punct":
      return { color: palette.punct };
    default:
      return { color: palette.text };
  }
}

/**
 * Pretty-print XML para que sea legible en el preview · si falla
 * (XML malformado), devuelve el texto crudo.
 *
 * Naive pero efectivo: re-indenta según los límites de tags. No es un
 * formateador completo · no maneja el contenido `text` entre tags al
 * 100% pero alcanza para CFDIs y XMLs simples.
 */
export function prettyXml(raw: string): string {
  try {
    const trimmed = raw.trim();
    if (!trimmed.startsWith("<")) return raw;
    const sin = trimmed.replace(/>\s+</g, "><");
    let depth = 0;
    let out = "";
    const tokens = sin.split(/(<[^>]+>)/g).filter(Boolean);
    for (const t of tokens) {
      if (/^<\/[^>]+>$/.test(t)) {
        depth = Math.max(depth - 1, 0);
        out += "  ".repeat(depth) + t + "\n";
      } else if (/^<[^!?][^>]*\/>$/.test(t)) {
        out += "  ".repeat(depth) + t + "\n";
      } else if (/^<[^!?][^>]*>$/.test(t)) {
        out += "  ".repeat(depth) + t + "\n";
        depth += 1;
      } else if (/^<\?xml/.test(t) || /^<!/.test(t)) {
        out += "  ".repeat(depth) + t + "\n";
      } else {
        out += "  ".repeat(depth) + t + "\n";
      }
    }
    return out.trim();
  } catch {
    return raw;
  }
}
